import { Chart, ChartDataset, TooltipItem, TooltipModel } from 'chart.js';
import { TooltipContentDOM } from '../BenchmarkTooltip/renderTooltip';

import { tooltipHandler } from '../../../../../utilities/common/chart';
import { BarChartMetaData } from '../../../../../shared/types/widget';
import { getAllBenchmarkDetails } from '../../../../../utilities/common/utils';
import { COLORS } from '../../../../../shared/constants/colors';
import {
  ConfigDataType,
  IBenchmarkType,
} from '../../../../../shared/types/filters';
import { ChartDatasetIndex } from '../../../../../shared/constants/chart';

interface YScaleTextOptions {
  font?: string;
  color?: string;
  yLabel?: string;
  y1Label?: string;
  offsetXLeft?: number;
  offsetXRight?: number;
  offsetY?: number;
}

export function getBarChartOptions({
  isBenchmarkEnabled,
  isMobile,
  meta,
  configData,
  benchmarkType,
  t,
}: {
  isBenchmarkEnabled: boolean;
  isMobile: boolean;
  meta: BarChartMetaData;
  configData: ConfigDataType;
  benchmarkType: IBenchmarkType;
  t: (key: string) => string;
}) {
  const GRID_COLOR = '#EBEBEB';
  const FONT_COLOR = '#404040';

  const uniqueRespondentsDataset =
    meta?.data?.datasets?.[ChartDatasetIndex?.RESPONDENT_INDEX];

  const uniqueRespondentsValues = Array.isArray(uniqueRespondentsDataset?.data)
    ? uniqueRespondentsDataset.data.map((v) => (typeof v === 'number' ? v : 0))
    : [];

  const y1Min = Math.min(...uniqueRespondentsValues, 0);
  const y1Max = Math.max(...uniqueRespondentsValues, 0);

  const { thriving, attention, global } = getAllBenchmarkDetails(
    meta?.meta,
    benchmarkType,
    configData,
  );

  // Annotations for chartJs
  const createAnnotationLine = (
    key: any,
    { color, value }: { color: string; value: number },
  ) => ({
    type: 'line',
    borderColor: color,
    borderWidth: 1,
    borderDash: [6, 6],
    label: {
      backgroundColor: color,
      content: value,
      display: true,
      position: 'end',
      xAdjust: 7,
    },
    scaleID: 'y',
    value,
  });

  const annotation = {
    line1: createAnnotationLine('line1', thriving),
    line2: createAnnotationLine('line2', attention),
    line3: createAnnotationLine('line3', global),
  };

  return {
    layout: {
      padding: {
        top: 35,
        right: 65,
        left: 16,
        bottom: 0,
      },
    },

    plugins: {
      tooltip: {
        filter: (tooltipItem: TooltipItem<'bar' | 'line'>) =>
          tooltipItem.dataset.type === 'bar',
        enabled: false,
        external: (ctx: { tooltip: TooltipModel<'bar' | 'line'> }) => {
          const BASE_HEIGHT = 58; // Reference bar height for normal tooltip offset
          const BASE_TRANSLATE_Y = -70; // Default translateY for base height
          const MAX_TRANSLATE_Y = -20; // Limit how close tooltip gets to the bar
          const STEP_SIZE = 10; // Each 10% above base height reduces the offset

          const { tooltip } = ctx;
          const item = tooltip.dataPoints?.[0];
          let barValue = 0;
          let label = '';
          let barIndex = 0;

          if (item) {
            barIndex = item.dataIndex;
            barValue = item.raw as number;
            label = item.label;
          }

          const uniqueRespondents =
            meta?.data.datasets?.[ChartDatasetIndex?.RESPONDENT_INDEX]?.data?.[
              barIndex
            ] ?? 0;

          const wrapper = document.createElement('div');
          wrapper.classList.add('chart-tooltip-wrapper');

          let translateYPercent = BASE_TRANSLATE_Y;

          if (item?.element) {
            const barHeight = item.raw as number;

            if (barHeight > BASE_HEIGHT) {
              const percentAbove =
                ((barHeight - BASE_HEIGHT) / BASE_HEIGHT) * 100;
              const reductionSteps = Math.floor(percentAbove / STEP_SIZE);

              translateYPercent = BASE_TRANSLATE_Y + reductionSteps * STEP_SIZE;

              // Cap the translateY to avoid getting too close to the bar
              translateYPercent = Math.min(translateYPercent, MAX_TRANSLATE_Y);
            }

            wrapper.style.transform = `translateY(${translateYPercent}%)`;
          }

          const content = TooltipContentDOM({
            meta,
            score: barValue,
            uniqueRespondents,
            title: label,
            benchmarkType,
            configData,
            t,
          });

          const colorSquare = document.createElement('span');
          colorSquare.classList.add('colorSquare');

          wrapper.appendChild(content);

          tooltipHandler(ctx, wrapper);
        },
      },

      datalabels: {
        color: '#ffffff',
        font: { size: 12, weight: '600' },
        anchor: 'end',
        align: 'start',
        display: (ctx: any) => ctx.dataset.type === 'bar',
        clamp: true,
        clip: false,
      },

      annotation: {
        annotations: isBenchmarkEnabled ? { ...annotation } : {},
      },

      legend: { display: false },
    },

    scales: {
      x: {
        title: {
          display: true,
          text: t('QUARTER'),
          color: COLORS['secondary-blue-700'],
          font: { family: "'Figtree', sans-serif", size: 12, weight: '500' },
          padding: { top: 10 },
        },
        ticks: { padding: 24, color: FONT_COLOR },
        grid: { display: false, drawTicks: false },
        border: { display: true, color: GRID_COLOR },
      },
      x1: {
        position: 'top',
        grid: { display: false, drawTicks: false },
        border: { display: true, color: GRID_COLOR },
        ticks: { display: false },
      },
      y: {
        suggestedMax: 100,
        grid: {
          display: true,
          drawTicks: false,
          color: GRID_COLOR,
          drawBorder: false,
        },
        border: { display: true, color: GRID_COLOR },
        ticks: {
          stepSize: 25,
          padding: 16,
          font: { family: "'Figtree', sans-serif", size: 12 },
          color: FONT_COLOR,
        },
      },
      y1: {
        position: 'right',
        min: y1Min,
        max: y1Max,
        grid: { display: false, drawTicks: false },
        border: { display: true, color: GRID_COLOR },
        ticks: {
          display: true,
          padding: 16,
          font: { family: "'Figtree', sans-serif", size: 12 },
          color: FONT_COLOR,
        },
      },
    },

    datasets: {
      line: {
        clip: false,
      },
      bar: {
        maxBarThickness: 40,
      },
    },

    responsive: true,
    maintainAspectRatio: false,
    borderRadius: '8',
  };
}

export const createYScaleTextPlugin = ({
  yLabel,
  y1Label,
}: {
  yLabel: string;
  y1Label: string;
}) => ({
  id: 'yScaleText',
  afterDraw(chart: Chart<'bar' | 'line'>) {
    const {
      ctx,
      chartArea: { top },
      scales: { y, y1 },
    } = chart;

    const yScaleTextOptions: YScaleTextOptions = {
      yLabel,
      y1Label,
      font: '500 12px "Figtree", sans-serif',
      color: COLORS['secondary-blue-700'],
      offsetXLeft: 0,
      offsetXRight: 0,
      offsetY: 20,
    };

    ctx.save();
    ctx.font = yScaleTextOptions.font!;
    ctx.fillStyle = yScaleTextOptions.color!;

    if (yScaleTextOptions.yLabel) {
      const textWidth = ctx.measureText(yScaleTextOptions.yLabel).width;
      const centerX = y.left + ((y as any).width ?? 0) / 2;
      ctx.fillText(
        yScaleTextOptions.yLabel,
        centerX - textWidth / 2 + (yScaleTextOptions.offsetXLeft ?? 0),
        top - (yScaleTextOptions.offsetY ?? 10),
      );
    }

    if (y1 && yScaleTextOptions.y1Label) {
      const textWidth = ctx.measureText(yScaleTextOptions.y1Label).width;
      const centerX = y1.left + ((y1 as any).width ?? 0) / 2;
      ctx.fillText(
        yScaleTextOptions.y1Label,
        centerX - textWidth / 2 + (yScaleTextOptions.offsetXRight ?? 0),
        top - (yScaleTextOptions.offsetY ?? 10),
      );
    }

    ctx.restore();
  },
});

export function createTrendLineDataset(
  showRespondentsLine: boolean,
  rawData: number[] = [],
): ChartDataset<'line'> {
  return {
    type: 'line',
    label: 'Trend Line',
    borderColor: COLORS['primary-dark-500'],
    borderWidth: 2,
    pointBackgroundColor: '#fff',
    pointBorderColor: COLORS['primary-dark-500'],
    pointRadius: 4,
    fill: false,
    tension: 0.4,
    data: rawData,
    hidden: !showRespondentsLine,
    order: 0,
    yAxisID: 'y1',
  };
}
